import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import z from 'zod';
import { useQuery } from '@tanstack/react-query';
import { VaultKeys, getAllVaultExports } from '../../../api/vault-api';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import GeneralDialog from '../../../shared/general-dialog';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { handleErrors } from '../../../utils/errors';
import CreateVaultExportForm, {
  CreateVaultExportFormValues,
} from './create-vault-export-form';
import { useVaultActions } from './hooks/use-vault-actions';
import { permissionByCorpus } from './vault-export-constants';

interface CreateVaultExportDialogProps {
  open: boolean;
  matterId: string;
  handleClose: () => void;
  matterName?: string;
}

export default function CreateVaultExportDialog({
  open,
  matterId,
  handleClose,
  matterName,
}: CreateVaultExportDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [submittedData, setSubmittedData] =
    useState<CreateVaultExportFormValues | null>(null);
  const { changeLogRoles, toolsRoles } = useUserSettings();

  const { data: exportsPage } = useQuery({
    queryKey: [VaultKeys.VaultExports, matterId],
    queryFn: () => getAllVaultExports(matterId),
    enabled: !!matterId,
  });

  const existingExportNames = useMemo(() => {
    return (exportsPage?.results || []).map((e) => e.name.toLowerCase());
  }, [exportsPage]);

  const exportFormSchema = useMemo(() => {
    return z
      .object({
        name: z.string().min(1, 'Export Name is required.'),
        corpus: z.string().min(1, 'Corpus is required.'),
        dataScope: z.string().min(1, 'Data Scope is required.'),
        searchMethod: z.string().min(1, 'Search Method is required.'),
        searchMethodTarget: z.string().optional(),
        startTime: z
          .string()
          .optional()
          .refine(
            (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
            'Invalid start date.'
          ),
        endTime: z
          .string()
          .optional()
          .refine(
            (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
            'Invalid end date.'
          ),
        terms: z.string().optional(),
        timeZone: z.string().min(1, 'Time Zone is required.'),
        exportFormat: z.string().optional(),
        vaultExcludeDrafts: z.boolean().optional(),
        includeChatSpaces: z.boolean().optional(),
        showIndirectAccess: z.boolean().optional(),
        matterRegion: z.string().optional(),
        methodTargetVersionDate: z
          .string()
          .optional()
          .refine(
            (val) => !val || /^\d{4}-\d{2}-\d{2}$/.test(val),
            'Invalid version date.'
          ),
        peopleQuery: z.array(z.string()).optional(),
        locationQuery: z.array(z.string()).optional(),
        minusWords: z.array(z.string()).optional(),
        responseStatuses: z.array(z.string()).optional(),
        sharedDrivesOption: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (existingExportNames.includes(data.name.trim().toLowerCase())) {
          ctx.addIssue({
            code: 'custom',
            message: `An export with the name '${data.name.trim()}' already exists in this matter.`,
            path: ['name'],
          });
        }

        if (
          data.searchMethod !== 'ENTIRE_ORG' &&
          !data.searchMethodTarget?.trim()
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'Target is required.',
            path: ['searchMethodTarget'],
          });
        }

        if (
          data.searchMethod === 'ORG_UNIT' &&
          data.searchMethodTarget?.trim() === '/'
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'Select an organizational unit other than the root.',
            path: ['searchMethodTarget'],
          });
        }

        if (
          data.startTime &&
          data.endTime &&
          /^\d{4}-\d{2}-\d{2}$/.test(data.startTime) &&
          /^\d{4}-\d{2}-\d{2}$/.test(data.endTime)
        ) {
          if (new Date(data.endTime) < new Date(data.startTime)) {
            ctx.addIssue({
              code: 'custom',
              message: 'End time cannot be before start time.',
              path: ['endTime'],
            });
          }
        }
      });
  }, [existingExportNames]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    getValues,
    setError,
    formState: { errors },
  } = useForm<CreateVaultExportFormValues>({
    resolver: zodResolver(exportFormSchema),
    defaultValues: {
      name: '',
      corpus: 'MAIL',
      dataScope: 'ALL_DATA',
      searchMethod: 'ACCOUNT',
      searchMethodTarget: '',
      terms: '',
      timeZone: 'America/New_York',
      exportFormat: 'MBOX',
      vaultExcludeDrafts: false,
      includeChatSpaces: false,
      showIndirectAccess: false,
      matterRegion: 'UNSPECIFIED',
      methodTargetVersionDate: '',
      peopleQuery: [],
      locationQuery: [],
      minusWords: [],
      responseStatuses: [],
      sharedDrivesOption: 'INCLUDED',
    },
  });

  const selectedCorpus = watch('corpus');
  const selectedSearchMethod = watch('searchMethod');

  const { createExport, isCreatingExport } = useVaultActions();

  const onSubmit: SubmitHandler<CreateVaultExportFormValues> = (data) => {
    const hasGenericChangelog =
      isAbleToSeeUIElement(toolsRoles, VaultRoles.CreateVaultExports.value) &&
      isAbleToSeeUIElement(changeLogRoles, VaultRoles.CreateVaultExports.value);

    const specificPermission = permissionByCorpus[data.corpus];
    const hasSpecificChangelog =
      specificPermission &&
      isAbleToSeeUIElement(toolsRoles, specificPermission) &&
      isAbleToSeeUIElement(changeLogRoles, specificPermission);

    if (hasGenericChangelog || hasSpecificChangelog) {
      setSubmittedData(data);
      setOpenChangeLogDialog(true);
    } else {
      createExport(
        { matterId, model: data, changeLogMessage },
        {
          onSuccess: () => {
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
            setSubmittedData(null);
            handleCloseDialog();
          },
          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.moreDetails ||
              error?.response?.data?.message ||
              '';
            if (errorMessage.includes('already exists in this matter')) {
              setError('name', {
                type: 'manual',
                message: errorMessage,
              });
            } else {
              handleErrors({
                error,
                snackbarMessage:
                  'There was an issue creating the vault export. Please refresh and try again.',
              });
              handleCloseDialog();
            }
          },
        }
      );
    }
  };

  const handleCloseDialog = () => {
    reset();
    setSubmittedData(null);
    handleClose();
  };

  return (
    <>
      <GeneralDialog
        open={open}
        handleClose={handleCloseDialog}
        title="Create Vault Export"
        maxWidth="md"
        dialogContent={
          <CreateVaultExportForm
            onSubmit={handleSubmit(onSubmit)}
            register={register}
            errors={errors}
            control={control}
            toolsRoles={toolsRoles}
            setValue={setValue}
            selectedSearchMethod={selectedSearchMethod}
            selectedCorpus={selectedCorpus}
            permissionByCorpus={permissionByCorpus}
            matterName={matterName}
          />
        }
        secondaryButton={{
          title: 'Cancel',
          action: handleCloseDialog,
          disabled: isCreatingExport,
          loading: false,
          shown: true,
        }}
        primaryButton={{
          title: 'Create',
          action: handleSubmit(onSubmit),
          disabled: isCreatingExport,
          loading: isCreatingExport,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setSubmittedData(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const exportModel = submittedData || getValues();
          createExport(
            { matterId, model: exportModel, changeLogMessage },
            {
              onSuccess: () => {
                setOpenChangeLogDialog(false);
                setChangeLogMessage('');
                setSubmittedData(null);
                handleCloseDialog();
              },
              onError: (error: any) => {
                const errorMessage =
                  error?.response?.data?.moreDetails ||
                  error?.response?.data?.message ||
                  '';
                if (errorMessage.includes('already exists in this matter')) {
                  setOpenChangeLogDialog(false);
                  setChangeLogMessage('');
                  setError('name', {
                    type: 'manual',
                    message: errorMessage,
                  });
                } else {
                  handleErrors({
                    error,
                    snackbarMessage:
                      'There was an issue creating the vault export. Please refresh and try again.',
                  });
                  setOpenChangeLogDialog(false);
                  setChangeLogMessage('');
                  handleCloseDialog();
                }
              },
            }
          );
        }}
        mutateLoading={isCreatingExport}
      />
    </>
  );
}
